import {Navigate, Outlet, useLocation} from 'react-router-dom';
import {useState, useEffect} from 'react';
import useAuthStore from '../stores/authStore';
import secureStorage from '../utils/secureStorage';
import {canAccessRoute, getFirstAccessibleRoute} from '../utils/permissionHelper';

const ProtectedRoute = () => {
    const {isAuthenticated, user, isInitialized} = useAuthStore();
    const [shouldRender, setShouldRender] = useState(false);
    const [redirectPath, setRedirectPath] = useState(null);
    const location = useLocation();

    useEffect(() => {
        // Don't run access check until the store has finished reading from secureStorage.
        // This prevents a race where the store hasn't loaded the token yet and the
        // user would be incorrectly redirected to /login on a plain page refresh.
        if (!isInitialized) return;

        const checkAccess = async () => {
            const currentPath = location.pathname;

            if (!isAuthenticated) {
                setRedirectPath('/login');
                setShouldRender(true);
                return;
            }

            // Skip account check for select-account page
            if (currentPath === '/select-account') {
                setShouldRender(true);
                return;
            }

            // For other protected routes, check for selected account
            const selectedAccount = await secureStorage.getItem('selectedAccount');
            if (!selectedAccount) {
                setRedirectPath('/select-account');
                setShouldRender(true);
                return;
            }

            // Attendre que l'utilisateur soit chargé avant de vérifier les permissions
            if (!user || !user.backoffice_permissions) {
                setShouldRender(true);
                return;
            }

            // Vérifier les permissions pour la route
            const hasAccess = canAccessRoute(currentPath, selectedAccount);

            if (!hasAccess) {
                const firstAccessible = getFirstAccessibleRoute(selectedAccount);
                if (firstAccessible !== currentPath) {
                    setRedirectPath(firstAccessible);
                } else {
                    setRedirectPath(null);
                }
                setShouldRender(true);
                return;
            }

            setRedirectPath(null);
            setShouldRender(true);
        };

        checkAccess();
    }, [isAuthenticated, isInitialized, user, location.pathname]);

    // Show nothing while the auth store is still loading from storage
    if (!isInitialized || !shouldRender) {
        return null;
    }

    if (redirectPath) {
        return <Navigate to={redirectPath} replace/>;
    }

    return <Outlet/>;
};

export default ProtectedRoute;
